import React from 'react';
import './CreateJobAdvanced.scss';
import {
  Card,
  CardHeader,
  CardContent,
  TextField,
  Grid,
  FormControlLabel,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Button,
  IconButton
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { createJobConfigHooks } from './hooks';
import { JobDetailsCard } from './components/JobDetailsCard';
import { NotificationsCard } from './components/NotificationsCard';
import { CronScheduleCard } from './components/CronScheduleCard';
import { KronosAuthCard } from './components/KronosAuthCard';
import { SftpCredentialsCard } from './components/SftpCredentialsCard';
import { CommaListField } from './components/CommaListField';
import { LRS_EXPORT_DEFAULTS, LRS_DEFAULT_FILE_NAMING_SCHEMA } from './lrsConfig';

const { useJobGeneralConfig, useJobConfig } = createJobConfigHooks({
  partner: 'LRS',
  defaultConfig: LRS_EXPORT_DEFAULTS
});

const ClientPlanCard = () => {
  const [companyName, setCompanyName] = useJobConfig(['companyName']);
  const [clientShortName, setClientShortName] = useJobConfig(['clientShortName']);
  const [planId, setPlanId] = useJobConfig(['planId']);
  return (
    <Card>
      <CardHeader subheader="Identifies which client this feed is for" title="Client & Plan" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="UKG company short name, used to run the reports and in notifications"
              label="Company short name"
              onChange={(e) => setCompanyName(e.target.value)}
              required
              value={companyName || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="Used in the file names and the payroll summary, e.g. RoveBrand"
              label="Client short name"
              onChange={(e) => setClientShortName(e.target.value)}
              required
              value={clientShortName || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="LRS PlanID for a client with a single plan, e.g. HCM01. Leave blank when the plans are listed below."
              label="Plan ID"
              onChange={(e) => setPlanId(e.target.value || undefined)}
              value={planId || ''}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const PlansCard = () => {
  const [_plans, setPlans] = useJobConfig(['plans']);
  const plans = _plans || [];
  const savePlans = (next) => setPlans(next.length > 0 ? next : undefined);
  const setPlanField = (i, field, value) =>
    savePlans(plans.map((plan, index) => (index === i ? { ...plan, [field]: value } : plan)));
  return (
    <Card>
      <CardHeader
        subheader="Only for clients with more than one LRS plan. Each plan gets its own set of files."
        title="Plans"
      />
      <CardContent>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name (LRS PlanID)</TableCell>
              <TableCell>Short name</TableCell>
              <TableCell>EINs</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map((plan, i) => (
              <TableRow key={i}>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Name"
                    onChange={(e) => setPlanField(i, 'name', e.target.value)}
                    placeholder="e.g. P69"
                    required
                    value={plan.name || ''}
                    variant="standard"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    label="Short name"
                    onChange={(e) => setPlanField(i, 'shortName', e.target.value)}
                    placeholder="e.g. AureusCapital"
                    required
                    value={plan.shortName || ''}
                    variant="standard"
                  />
                </TableCell>
                <TableCell>
                  <CommaListField
                    helperText="Federal EINs or legal names, as on the Retirement report. Comma separated."
                    label="EINs"
                    onChange={(eins) => setPlanField(i, 'eins', eins)}
                    placeholder="EINs (Comma separated)"
                    required
                    value={plan.eins}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    aria-label={`Remove plan ${i + 1}`}
                    onClick={() => savePlans(plans.filter((_, index) => index !== i))}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Button
          onClick={() => savePlans([...plans, { name: '', shortName: '', eins: [] }])}
          startIcon={<AddIcon />}
          sx={{ marginTop: 1 }}
          variant="outlined"
        >
          Add plan
        </Button>
      </CardContent>
    </Card>
  );
};

const ReportsCard = () => {
  const [retirement, setRetirement] = useJobConfig(['report', 'retirement']);
  const [workersComp, setWorkersComp] = useJobConfig(['report', 'workers_comp']);
  return (
    <Card>
      <CardHeader subheader="The UKG reports the export is built from" title="Reports" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="UKG report ID of the Retirement report"
              label="Retirement report"
              onChange={(e) => setRetirement(e.target.value)}
              required
              value={retirement || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="UKG report ID of the Workers Comp report"
              label="Workers comp report"
              onChange={(e) => setWorkersComp(e.target.value)}
              required
              value={workersComp || ''}
              variant="standard"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const SSN_FORMATS = [
  { value: 'dashes', label: '###-##-#### (dashes)' },
  { value: 'digits', label: '######### (9 digits)' }
];

const ExportFileCard = () => {
  const [fileNamingSchema, setFileNamingSchema] = useJobConfig(['fileNamingSchema']);
  const [includeHeader, setIncludeHeader] = useJobConfig(['includeHeader']);
  const [ssnFormat, setSsnFormat] = useJobConfig(['ssnFormat']);
  return (
    <Card>
      <CardHeader subheader="What the files sent to LRS look like" title="Export file" />
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              helperText={`Blank uses ${LRS_DEFAULT_FILE_NAMING_SCHEMA}. Tokens: {PLAN_ID} {PAY_DATE} {PAYROLL_ID} {CLIENT_SHORT_NAME}`}
              label="File naming schema"
              onChange={(e) => setFileNamingSchema(e.target.value || undefined)}
              placeholder={LRS_DEFAULT_FILE_NAMING_SCHEMA}
              value={fileNamingSchema || ''}
              variant="standard"
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <TextField
              fullWidth
              helperText="The format the Retirement report produces. It is checked, not converted."
              label="SSN format"
              onChange={(e) => setSsnFormat(e.target.value)}
              select
              // eslint-disable-next-line react/jsx-sort-props
              SelectProps={{ native: true }}
              value={ssnFormat || 'dashes'}
              variant="standard"
            >
              {SSN_FORMATS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item md={6} xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeHeader !== false}
                  onChange={(e) => setIncludeHeader(e.target.checked)}
                />
              }
              label="Include header"
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const LrsPayrollExportForm = () => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <JobDetailsCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
      <Grid item xs={12}>
        <ClientPlanCard />
      </Grid>
      <Grid item xs={12}>
        <PlansCard />
      </Grid>
      <Grid item xs={12}>
        <ReportsCard />
      </Grid>
      <Grid item xs={12}>
        <KronosAuthCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <SftpCredentialsCard
          subheader="Where the export files are uploaded"
          useJobConfig={useJobConfig}
        />
      </Grid>
      <Grid item xs={12}>
        <ExportFileCard />
      </Grid>
      <Grid item xs={12}>
        <NotificationsCard useJobConfig={useJobConfig} />
      </Grid>
      <Grid item xs={12}>
        <CronScheduleCard useJobGeneralConfig={useJobGeneralConfig} />
      </Grid>
    </Grid>
  );
};
